// quantiix-shell/src/config/routes.js

/**
 * Route configuration - Single source of truth for all MFE routes
 */
export const ROUTES = {
    // Dashboard MFE
    companies: () => '/app/dashboard',
    factories: (companyId) => `/app/dashboard/company/${companyId}`,
    ous: (factoryId) => `/app/dashboard/factory/${factoryId}`,

    // PublicView MFE
    publicview: (ouId) => `/app/publicview/${ouId}`,

    // Machine MFE
    machine: (machineId) => `/app/machine/${machineId}`,

    // Config MFE (NEW)
    config: {
        companies: () => '/app/config',
        factories: (companyId) => `/app/config/company/${companyId}`,
        ous: (factoryId) => `/app/config/factory/${factoryId}`,
        publicviews: (ouId) => `/app/config/ou/${ouId}`,
    }
};

/**
 * Parse current pathname to determine entity type and ID
 *
 * @returns {Object} { entityType, entityId, source }
 *   - source: 'dashboard' | 'config' | 'publicview' | 'machine'
 */
export function parseCurrentRoute(pathname) {
    const patterns = [
        // Machine MFE (unchanged)
        { regex: /^\/app\/machine\/([^/]+)$/, type: 'machine', source: 'machine' },

        // PublicView MFE (unchanged)
        { regex: /^\/app\/publicview\/([^/]+)$/, type: 'publicview', source: 'publicview' },

        // Dashboard MFE (unchanged - add source)
        { regex: /^\/app\/dashboard\/ou\/([^/]+)$/, type: 'ou', source: 'dashboard' },
        { regex: /^\/app\/dashboard\/factory\/([^/]+)$/, type: 'factory', source: 'dashboard' },
        { regex: /^\/app\/dashboard\/company\/([^/]+)$/, type: 'company', source: 'dashboard' },
        { regex: /^\/app\/dashboard\/?$/, type: 'companies', source: 'dashboard' },

        // Config MFE (NEW)
        { regex: /^\/app\/config\/ou\/([^/]+)$/, type: 'ou', source: 'config' },
        { regex: /^\/app\/config\/factory\/([^/]+)$/, type: 'factory', source: 'config' },
        { regex: /^\/app\/config\/company\/([^/]+)$/, type: 'company', source: 'config' },
        { regex: /^\/app\/config\/?$/, type: 'companies', source: 'config' },
    ];

    for (const { regex, type, source } of patterns) {
        const match = pathname.match(regex);
        if (match) {
            return {
                entityType: type,
                entityId: match[1] || null,
                source
            };
        }
    }

    return { entityType: null, entityId: null, source: null };
}
/**
 * Mirror the user's current location into Config.
 *
 * The Config MFE has the same hierarchy as the Dashboard minus the machine
 * level, so every view has a counterpart: company -> its factory list, factory
 * -> its OU list, OU/PublicView -> that OU's PublicView editor. A machine has
 * no counterpart, so it resolves to its own OU (the nearest configurable thing)
 * — pass `machineOuId` for that.
 *
 * Routes outside the hierarchy (reports, shifts, settings, home) have nothing to
 * mirror, so they fall back to the session scope, deepest level first.
 *
 * @param {string} pathname - Where the user is now
 * @param {Object} [options]
 * @param {string} [options.machineOuId] - OU owning the machine, for machine routes
 * @param {Object} [options.scope] - Session scope { ouId, factoryId, companyId }
 * @returns {string} - A Config route
 */
export function configPathFor(pathname, { machineOuId, scope = {} } = {}) {
    const { entityType, entityId, source } = parseCurrentRoute(pathname);

    if (source !== 'config') {
        switch (entityType) {
            case 'company':
                return ROUTES.config.factories(entityId);
            case 'factory':
                return ROUTES.config.ous(entityId);
            case 'ou':
            case 'publicview':
                return ROUTES.config.publicviews(entityId);
            case 'machine':
                if (machineOuId) return ROUTES.config.publicviews(machineOuId);
                break;
            case 'companies':
                return ROUTES.config.companies();
            default:
                break;
        }
    }

    // Nothing to mirror — resume the deepest scope the session still holds.
    if (scope.ouId) return ROUTES.config.publicviews(scope.ouId);
    if (scope.factoryId) return ROUTES.config.ous(scope.factoryId);
    if (scope.companyId) return ROUTES.config.factories(scope.companyId);

    return ROUTES.config.companies();
}

/**
 * Mirror a Config location back into the app — the inverse of configPathFor.
 *
 * @param {string} pathname - A Config route
 * @returns {string|null} - The matching app route, or null if it isn't one
 */
export function appPathFor(pathname) {
    const { entityType, entityId, source } = parseCurrentRoute(pathname);

    if (source !== 'config') return null;

    switch (entityType) {
        case 'company':
            return ROUTES.factories(entityId);
        case 'factory':
            return ROUTES.ous(entityId);
        case 'ou':
            return ROUTES.publicview(entityId);
        case 'companies':
            return ROUTES.companies();
        default:
            return null;
    }
}
